import type {
  AudioBatchItemProgress,
  AudioBatchItemStatus,
  AudioBatchItemSubStage,
} from './types'

/**
 * The minimum an item must expose to be placed on the progress scale.
 *
 * Structural rather than `AudioBatchItem` so surfaces carrying a thinner shape
 * (the anonymous trial result, which has a stage and a duration and nothing
 * else) share this model instead of growing a second one. `status` is widened to
 * `string` deliberately: a value this build has not been taught about resolves
 * to "unknown", which renders as indeterminate — the honest answer — rather than
 * throwing or silently landing in the wrong band.
 */
export interface StageProgressItem {
  status: AudioBatchItemStatus | (string & {})
  startedAt: string | null
  audioDurationSecs: number | null
  progress?: AudioBatchItemProgress | null
}

/**
 * How often the client asks the server where an item is.
 *
 * This and {@link PROGRESS_TRANSITION_MS} are ONE decision, not two: the CSS
 * transition must outlast the poll interval, or the line reaches its target and
 * stands still until the next sample — which is exactly the "looks broken" bug
 * this module exists to remove. Change one and you must change the other, which
 * is why they live on adjacent lines and a test asserts the relationship.
 */
export const PROGRESS_POLL_MS = 2000

/** Must exceed {@link PROGRESS_POLL_MS}. Mirrored by `--progress-transition` in CSS. */
export const PROGRESS_TRANSITION_MS = 2400

/** The four steps a user is shown. Deliberately coarser than the pipeline's own stages. */
export type ProgressStageKey = 'queued' | 'media' | 'transcribing' | 'done'

export type ProgressStageState = 'done' | 'active' | 'todo' | 'failed'

export interface ItemStageStep {
  key: ProgressStageKey
  state: ProgressStageState
}

export interface ItemStageView {
  /** Always four entries, in order. */
  stages: ItemStageStep[]
  /** Index into {@link stages} of the step that is running (or that failed). */
  stageIndex: number
  /** 0..1 position WITHIN the current step — what the detail-page spine measures. */
  stageLocal: number
  stageLabelKey: string
  subStageLabelKey: string | null
  /** Global 0..100, truncated. Null means "not knowable yet" — see {@link indeterminate}. */
  percent: number | null
  /**
   * True when there is no trustworthy percentage. Rendered differently from 0%
   * on purpose: 0% reads as "hasn't started", which is a different (and usually
   * wrong) statement than "we don't know yet".
   */
  indeterminate: boolean
  failed: boolean
  complete: boolean
  /** Seconds spent in the CURRENT step, or null when there is no clock to read. */
  elapsedSecs: number | null
}

const STAGE_KEYS: readonly ProgressStageKey[] = ['queued', 'media', 'transcribing', 'done']

/**
 * The global scale. Widths encode how long each step actually takes, so a slow
 * stretch reads as a long road rather than a stall — transcription owns 20→97
 * because it genuinely dominates the wall clock.
 *
 * Every displayed number comes from this one table: the row percentage, the
 * detail-page percentage and the spine position cannot disagree because there is
 * nothing to disagree with.
 */
const STAGE_BOUNDS: Record<ProgressStageKey, { from: number; to: number }> = {
  queued: { from: 0, to: 6 },
  media: { from: 6, to: 20 },
  transcribing: { from: 20, to: 97 },
  // 97→100 belongs to the completion transition, not to any reported work.
  done: { from: 97, to: 100 },
}

/** A band is one reportable step's slice of the global scale. */
interface Band {
  stage: ProgressStageKey
  from: number
  to: number
  subLabel: string | null
  /**
   * Interpolation constant (seconds) used ONLY when the server has no recorded
   * history for the step. Deliberately crude: `max(floor, coefficient × media
   * seconds)`. Being wrong here costs a line that creeps a little fast or slow;
   * it never invents a stage or an ETA, and a real measurement always wins.
   */
  fallbackTau: (audioDurationSecs: number) => number
}

const SUB_LABEL = (name: string) => `transcriptions.progress.subStage.${name}`

const SUB_STAGE_BANDS: Record<AudioBatchItemSubStage, Band> = {
  // Fetching the audio is part of "speech recognition" as far as anyone outside
  // the pipeline is concerned — it is not a step users asked about.
  preparing_audio: {
    stage: 'transcribing',
    from: 20,
    to: 26,
    subLabel: SUB_LABEL('speechRecognition'),
    fallbackTau: (d) => Math.max(15, 0.02 * d),
  },
  stt: {
    stage: 'transcribing',
    from: 26,
    to: 72,
    subLabel: SUB_LABEL('speechRecognition'),
    fallbackTau: (d) => Math.max(15, 0.12 * d),
  },
  // These two steps share ONE label. They are two separate passes internally,
  // and labelling each one would describe how the work is done rather than what
  // the user gets — a detail that is neither their concern nor a promise worth
  // making.
  //
  // The BANDS stay separate on purpose. Together these are the second longest
  // stretch of the pipeline (measured p50 ~51s and ~61s), so one merged band
  // would leave the bar advancing under an unchanging label for about two
  // minutes — the stall this design exists to remove. Two bands, one label: the
  // label holds steady while the number keeps moving.
  cue_segmentation: {
    stage: 'transcribing',
    from: 72,
    to: 86,
    subLabel: SUB_LABEL('tidyingTranscript'),
    fallbackTau: (d) => Math.max(15, 0.06 * d),
  },
  enhancement: {
    stage: 'transcribing',
    from: 86,
    to: 93,
    subLabel: SUB_LABEL('tidyingTranscript'),
    fallbackTau: (d) => Math.max(15, 0.05 * d),
  },
  finalizing: {
    stage: 'transcribing',
    from: 93,
    to: 97,
    subLabel: SUB_LABEL('wrappingUp'),
    fallbackTau: (d) => Math.max(10, 0.01 * d),
  },
}

/** Used until the pipeline reports a sub-stage — the coarse status still locates the item. */
const STATUS_BANDS: Partial<Record<AudioBatchItemStatus, Band>> = {
  pending: {
    stage: 'queued',
    from: 0,
    to: 6,
    subLabel: null,
    fallbackTau: () => 30,
  },
  downloading: {
    stage: 'media',
    from: 6,
    to: 12,
    subLabel: SUB_LABEL('downloading'),
    fallbackTau: (d) => Math.max(15, 0.03 * d),
  },
  extracting_audio: {
    stage: 'media',
    from: 12,
    to: 18,
    subLabel: SUB_LABEL('extractingAudio'),
    fallbackTau: (d) => Math.max(15, 0.02 * d),
  },
  audio_ready: {
    stage: 'media',
    from: 18,
    to: 20,
    subLabel: SUB_LABEL('gettingReady'),
    fallbackTau: () => 20,
  },
  // The pipeline reports `preparing_audio` on entry, so seeing this status with
  // no sub-stage means the claim just landed.
  transcribing: SUB_STAGE_BANDS.preparing_audio,
  // Never written by the current server; mapped rather than special-cased so an
  // older deployment mid-rollout still renders somewhere sensible.
  enhancing: SUB_STAGE_BANDS.enhancement,
}

export function stageLabelKey(stage: ProgressStageKey): string {
  return `transcriptions.progress.stage.${stage}`
}

/**
 * Turn one polled item into everything the three progress surfaces render.
 *
 * Pure and time-parameterised: given the same item and the same `nowMs` it
 * always answers the same thing, and the caller (not this module) owns the tick.
 * That is what makes the monotonicity guarantee testable rather than hoped for.
 */
export function deriveItemStageView(item: StageProgressItem, nowMs: number): ItemStageView {
  if (item.status === 'completed') return completedView()
  if (item.status === 'failed') return failedStageView(item.progress)

  const progress = item.progress ?? null
  const band = resolveBand(item.status, progress?.subStage ?? null)
  const startedAtMs = parseMs(item.startedAt)

  // "Queued" is the one step that genuinely is not moving, and nothing has
  // claimed the row yet when there is no start time — in both cases a number
  // would be a fabrication.
  const indeterminate = item.status === 'pending' || startedAtMs === null || band === null

  const stepStartedMs = parseMs(progress?.subStageStartedAt ?? null) ?? startedAtMs
  const elapsedSecs = stepStartedMs === null ? null : Math.max(0, (nowMs - stepStartedMs) / 1000)

  if (band === null || indeterminate) {
    const stage: ProgressStageKey = band?.stage ?? 'queued'
    return {
      stages: stepsFor(stage, null),
      stageIndex: STAGE_KEYS.indexOf(stage),
      stageLocal: 0,
      stageLabelKey: stageLabelKey(stage),
      subStageLabelKey: band?.subLabel ?? null,
      percent: null,
      indeterminate: true,
      failed: false,
      complete: false,
      elapsedSecs: elapsedSecs === null ? null : Math.floor(elapsedSecs),
    }
  }

  const counted = progress?.subStage ? clamp01(progress.fraction) : null
  const tau = progress?.expectedSecs ?? band.fallbackTau(item.audioDurationSecs ?? 0)
  const interpolated = elapsedSecs === null ? 0 : asymptotic(elapsedSecs, tau)
  // Whichever is further along wins: a counted fraction is the truth when the
  // step can count, and the clock keeps the line alive when it cannot.
  const local = Math.max(counted ?? 0, interpolated)
  const percent = Math.floor(band.from + (band.to - band.from) * local)

  return {
    stages: stepsFor(band.stage, null),
    stageIndex: STAGE_KEYS.indexOf(band.stage),
    stageLocal: localWithinStage(band.stage, percent),
    stageLabelKey: stageLabelKey(band.stage),
    subStageLabelKey: band.subLabel,
    percent,
    indeterminate: false,
    failed: false,
    complete: false,
    elapsedSecs: elapsedSecs === null ? null : Math.floor(elapsedSecs),
  }
}

function completedView(): ItemStageView {
  return {
    stages: STAGE_KEYS.map((key) => ({ key, state: 'done' as const })),
    stageIndex: STAGE_KEYS.length - 1,
    stageLocal: 1,
    stageLabelKey: stageLabelKey('done'),
    subStageLabelKey: null,
    percent: 100,
    indeterminate: false,
    failed: false,
    complete: true,
    elapsedSecs: null,
  }
}

/**
 * A failed item is frozen, never interpolated: the point it died at is
 * information, and a line that keeps creeping after death is a lie. Nothing here
 * reads the clock.
 *
 * Takes the progress alone rather than a whole item so the history LIST — whose
 * rows are a different, thinner shape — can render the same freeze without
 * needing a status poll it will never get for a row that is already dead.
 */
export function failedStageView(
  progress: AudioBatchItemProgress | null | undefined
): ItemStageView {
  const subStage = progress?.subStage ?? null
  const band = subStage ? SUB_STAGE_BANDS[subStage] : null

  if (band === null) {
    // No recorded progress on a failed item is itself evidence: the server writes
    // it as soon as transcription begins, so this one stopped at or before media
    // preparation. Reported at the earliest point consistent with that rather
    // than at an invented midpoint.
    const stage: ProgressStageKey = 'media'
    return {
      stages: stepsFor(stage, stage),
      stageIndex: STAGE_KEYS.indexOf(stage),
      stageLocal: 0,
      stageLabelKey: stageLabelKey(stage),
      subStageLabelKey: null,
      percent: STAGE_BOUNDS[stage].from,
      indeterminate: false,
      failed: true,
      complete: false,
      elapsedSecs: null,
    }
  }

  const local = clamp01(progress?.fraction ?? null) ?? 0
  const percent = Math.floor(band.from + (band.to - band.from) * local)
  return {
    stages: stepsFor(band.stage, band.stage),
    stageIndex: STAGE_KEYS.indexOf(band.stage),
    stageLocal: localWithinStage(band.stage, percent),
    stageLabelKey: stageLabelKey(band.stage),
    subStageLabelKey: band.subLabel,
    percent,
    indeterminate: false,
    failed: true,
    complete: false,
    elapsedSecs: null,
  }
}

function resolveBand(
  status: StageProgressItem['status'],
  subStage: AudioBatchItemSubStage | null
): Band | null {
  if (subStage) return SUB_STAGE_BANDS[subStage]
  return STATUS_BANDS[status as AudioBatchItemStatus] ?? null
}

function stepsFor(current: ProgressStageKey, failedAt: ProgressStageKey | null): ItemStageStep[] {
  const currentIndex = STAGE_KEYS.indexOf(current)
  return STAGE_KEYS.map((key, index) => {
    if (failedAt !== null && key === failedAt) return { key, state: 'failed' as const }
    if (index < currentIndex) return { key, state: 'done' as const }
    if (index === currentIndex) return { key, state: 'active' as const }
    return { key, state: 'todo' as const }
  })
}

function localWithinStage(stage: ProgressStageKey, percent: number): number {
  const { from, to } = STAGE_BOUNDS[stage]
  if (to <= from) return 1
  return clampUnit((percent - from) / (to - from))
}

/**
 * Ceiling on time-based interpolation. A clock can say "still going"; it can
 * never say "finished". Without this, a step that overruns its estimate by
 * enough eventually saturates the exponential to exactly 1.0 in floating point
 * and the line parks on the next band's boundary — reading as a completed step
 * while the work is still running. Only real evidence (a counted fraction, or
 * the status moving on) is allowed to reach a band's end.
 */
const INTERPOLATION_CEILING = 0.99

/**
 * Approaches {@link INTERPOLATION_CEILING} without reaching it, so a step that
 * runs long keeps moving and still cannot spill into the next one. `tau` is
 * where it passes ~63% of that ceiling.
 */
function asymptotic(elapsedSecs: number, tau: number): number {
  if (!Number.isFinite(tau) || tau <= 0) return 0
  return (1 - Math.exp(-elapsedSecs / tau)) * INTERPOLATION_CEILING
}

function clamp01(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  return clampUnit(value)
}

function clampUnit(value: number): number {
  return Math.min(1, Math.max(0, value))
}

function parseMs(iso: string | null | undefined): number | null {
  if (!iso) return null
  const ms = Date.parse(iso)
  return Number.isNaN(ms) ? null : ms
}

/**
 * What a row shows before anything is known about it — a fresh page load, or the
 * moment before the first poll answers.
 *
 * This exists to kill the old placeholder percentages (`?? 10`, `?? 50`). A made
 * up number is worse than none: it is indistinguishable from a real reading, so
 * the user cannot tell "10% done" from "we have not asked yet".
 */
export function pendingStageView(): ItemStageView {
  return {
    stages: stepsFor('queued', null),
    stageIndex: 0,
    stageLocal: 0,
    stageLabelKey: stageLabelKey('queued'),
    subStageLabelKey: null,
    percent: null,
    indeterminate: true,
    failed: false,
    complete: false,
    elapsedSecs: null,
  }
}
